import React from 'react';
import {
    DragDropContext,
    Draggable,
    DraggableProvidedDragHandleProps,
    DraggableStateSnapshot,
    DraggingStyle,
    Droppable,
    DropResult,
    NotDraggingStyle,
} from 'react-beautiful-dnd';
import { AnyTag, DraggableItemData } from './types';

/**
 * limit movement to the y-axis
 */
const getItemStyle = (draggableStyle: DraggingStyle|NotDraggingStyle) => {
    const { transform } = draggableStyle;
    let activeTransform = {};
    if (transform) {
        activeTransform = {
            transform: `translate(0, ${transform.substring(
                transform.indexOf(',') + 1,
                transform.indexOf(')')
            )})`,
        };
    }
    return {
        ...draggableStyle,
        ...activeTransform,
    };
};

export interface DraggableListProps<T extends DraggableItemData> {
    id: string;
    itemRenderer: (data: T, dragHandleProps: DraggableProvidedDragHandleProps, draggableState: DraggableStateSnapshot) => React.ReactNode;
    data: T[];
    onChange: (data: T[]) => void;
    header?: React.ReactChild;
    listComponent?: AnyTag;
    listItemComponent?: AnyTag;
}

export const DraggableList = <T extends DraggableItemData>(props: DraggableListProps<T>): JSX.Element => {
    const {
        listComponent: ListComponent = 'div',
        listItemComponent: ListItemComponent = 'div',
    } = props;

    const handleDragEnd = (result: DropResult): void => {
        const { destination, source } = result;

        if (!destination) {
            return;
        }

        if (destination.droppableId === source.droppableId && destination.index === source.index) {
            // dropped on same item
            return;
        }

        const newData = [ ...props.data ];
        newData.splice(source.index, 1);
        newData.splice(destination.index, 0, props.data[source.index]);
        props.onChange(newData);
    };

    return (
        <DragDropContext
            onDragEnd={handleDragEnd}
        >
            <Droppable
                direction="vertical"
                droppableId={props.id}
            >
                {provided => (
                    <ListComponent ref={provided.innerRef}>
                        {props.data.map((item, i) => {
                            return (
                                <Draggable
                                    key={item.id}
                                    draggableId={item.id}
                                    index={i}
                                >
                                    {(provided, snapshot) => (
                                        <ListItemComponent
                                            {...provided.draggableProps}
                                            style={getItemStyle(provided.draggableProps.style)}
                                            ref={provided.innerRef}
                                        >
                                            {props.itemRenderer(item, provided.dragHandleProps, snapshot)}
                                        </ListItemComponent>
                                    )}
                                </Draggable>
                            );
                        })}
                        {provided.placeholder}
                    </ListComponent>
                )}
            </Droppable>
        </DragDropContext>
    );
};
